<script lang="ts">
  import AddRepWithSalesRepList from "@retailer/svelte/Shared/AddRepWithSalesRepList.svelte";
  import ConnectDistributorModal from "@retailer/svelte/Settings/DistributorContacts/ConnectDistributorModal/ConnectDistributorModal.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import IntegratedTooltipIcon from "@shared/svelte/Components/IntegratedTooltipIcon.svelte";
  import OneWayMessageNotifications from "@retailer/svelte/ProductLines/Index/OneWayMessageNotifications.svelte";
  import OrderRequirements from "@retailer/svelte/Shared/DistributorInfo/OrderRequirements.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";

  import AddRepModalService from "@shared/svelte/Modules/AddRepModalService";
  import CloudinaryImageService from "@shared/svelte/Modules/CloudinaryImageService";
  import formatPhone from "@shared/filters/phone.js";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";

  import { getAddressString } from "@retailer/services/distributor_service";

  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";

  import type { Distributor } from "@shared/types/Distributor";
  import type { RetailerDistributorSummary } from "@shared/types/RetailerDistributor";

  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import {
    shouldHideAllDistributorInfo,
    getRetailerUserAuthenticationStatus,
    getStockSiteLocation,
  } from "@shared/services/retailer_user_authentication_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  import { params } from "@roxi/routify";
  import { onDestroy } from "svelte";
  import { OHLQ_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

  export let distributor: Distributor;
  export let authStatus: string;

  let unsubscribe = () => {};
  if (!distributor) {
    unsubscribe = DistributorsStore.subscribe(store => {
      distributor = store.find(d => d.id == $params.distributors?.[0]) || {};
    });
  }

  onDestroy(() => {
    unsubscribe();
  });

  let retailerDistributor: RetailerDistributorSummary;
  const getRetailerDistributor = async () => {
    if (!distributor?.id) return;

    const response = await HttpPromise({
      url: "/api/retailer/retailer_distributors",
      method: "GET",
      data: {
        distributor_id: distributor.id,
        limit: 1,
      },
    });

    if (response && response[0]) retailerDistributor = response[0];
  };

  getRetailerDistributor();

  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  let retailerAuth;

  $: retailerAuth = $RetailerUserAuthenticationsStore.find(a => a.distributor_id == distributor.id);
  $: hideAllDistributorInfo =
    hasRetailerUserAuthRollout &&
    distributor?.id &&
    shouldHideAllDistributorInfo($RetailerUserAuthenticationsStore, [distributor.id]);
  $: authStatus =
    hasRetailerUserAuthRollout &&
    distributor?.id &&
    getRetailerUserAuthenticationStatus($RetailerUserAuthenticationsStore, [distributor.id]);

  const service = new AddRepModalService();

  let showConnectDistributorModal = false;
  let selectedDistributor = null;
  let selectedReps = [];

  const submit = service.submit();

  const handleEvent = event => {
    selectedDistributor = event.detail;
    showConnectDistributorModal = true;
  };

  const imageLoadFailed = _event => {
    distributor.cloudinary_image_url = null;
  };

  $: if (!showConnectDistributorModal) {
    selectedReps = [];
  }
  $: showSalesReps =
    !hasRetailerUserAuthRollout ||
    !distributor?.require_retailer_user_authentication ||
    (hasRetailerUserAuthRollout &&
      authStatus == "valid" &&
      distributor?.id !== OHLQ_DISTRIBUTOR_ID);
  $: showMyAgency =
    hasRetailerUserAuthRollout && authStatus == "valid" && distributor?.id === OHLQ_DISTRIBUTOR_ID;
</script>

<style>
  @media (min-width: 700px) {
    .right-section {
      max-width: 52%;
    }
  }

  .distro-name {
    align-items: center !important;
  }
</style>

<div
  id="distributor-info-header"
  class="fizz-container"
  class:fizz-stack-32={!distributor.ordering_disabled}
  class:fizz-stack-12={distributor.ordering_disabled}
>
  <div class="fizz-flex-row-responsive fizz-bordered fizz-surface">
    <div
      class="distro-name fizz-flex-row-responsive fizz-flex-item fizz-inset-square-32 fizz-stack-0"
      class:fizz-border-bottom={$LayoutStore.isMobile}
      class:fizz-inset-squish-24={$LayoutStore.isMobile}
    >
      {#if distributor.cloudinary_image_url}
        <img
          on:error={imageLoadFailed}
          class="logo"
          src={CloudinaryImageService.getUrl(distributor.cloudinary_image_url, 75, 75)}
          alt="Logo"
        />
      {:else}
        <Icon
          type="DistributorLogoPlaceholder"
          height={72}
          width={72}
          extraClassNames="fizz-align-self-center"
        />
      {/if}
      <div>
        <h1
          class="fizz-flex-row-4 fizz-stack-8 fizz-heading-2"
          class:fizz-heading-3={$LayoutStore.isMobile}
          class:fizz-justify-center={$LayoutStore.isMobile}
          class:fizz-text-center={$LayoutStore.isMobile}
        >
          <IntegratedTooltipIcon {distributor} />
          <span>
            {distributor.name}
          </span>
        </h1>

        {#if showSalesReps}
          <div class="fizz-flex-row" class:fizz-justify-center={$LayoutStore.isMobile}>
            <AddRepWithSalesRepList
              bind:distributor
              {retailerDistributor}
              asButton={true}
              source="distributor_catalog"
              on:openConnectDistributorModal={handleEvent}
            />
          </div>
        {/if}

        {#if showMyAgency}
          {#await getStockSiteLocation(retailerAuth.distributor_id, retailerAuth.account_number) then data}
            <strong>My Agency:</strong>
            <UIRouterLink stateName={"layout.accounts"}>
              <strong>
                {data.name || ""}
              </strong>
            </UIRouterLink>
          {/await}
        {/if}
      </div>
    </div>
    <div
      class="fizz-inset-square-32 fizz-flow-8 right-section"
      class:fizz-inset-squish-24={$LayoutStore.isMobile}
    >
      <div
        class="fizz-flex-row-4 fizz-justify-center"
        class:fizz-justify-end={$LayoutStore.isDesktop}
      >
        {#if distributor.contact_phone && distributor.contact_phone.length > 0}
          <p class="fizz-no-wrap">
            {formatPhone(distributor.contact_phone)}
          </p>
          <p>&bull;</p>
        {/if}
        <p class="fizz-truncate" style="--max-width: 100%">
          {getAddressString(distributor)}
        </p>
      </div>
      <div
        class="fizz-flex-row-0 fizz-justify-center"
        class:fizz-justify-end={$LayoutStore.isDesktop}
      >
        <OrderRequirements
          distributorName={distributor.name}
          cutoffTime={distributor.next_cutoff}
          minPurchase={distributor.minimum_dollars_per_order}
          minQuantity={distributor.minimum_quantity_per_order}
          notes={distributor.ordering_notes}
          isMobile={$LayoutStore.isMobile}
        />
      </div>
      {#if (distributor.uses_storefronts || distributor.has_storefront) && !hideAllDistributorInfo}
        <div
          class="fizz-flex-row-0 fizz-justify-center"
          class:fizz-justify-end={$LayoutStore.isDesktop}
        >
          <UIRouterLink
            classes="fizz-text-strong"
            stateName="layout.storefront"
            params={{ distributor_id: distributor.id }}
            useTrackClick
            eventName="click"
            targetObjectType="distributor_catalog_visit_storefront_link"
          >
            Visit the distributor’s storefront
          </UIRouterLink>
        </div>
      {/if}
    </div>
  </div>
  {#if distributor?.id == OHLQ_DISTRIBUTOR_ID}
    <OneWayMessageNotifications {distributor} {authStatus} />
  {/if}
</div>

{#if distributor?.ordering_disabled}
  <div class="fizz-alert-warning fizz-alert--small">
    Provi is temporarily not accepting orders for {distributor.name}, but you can still add items to
    your cart to order later. Contact your rep for more information.
  </div>
{/if}

<ConnectDistributorModal
  bind:showConnectDistributorModal
  bind:selectedDistributor
  bind:selectedReps
  {submit}
  addNewSalesRep={service.addNewSalesRep}
  providedDistributor={true}
  addSalesRepErrorCallback={service.addSalesRepErrorCallback}
  submitErrorCallback={service.errorCallback}
/>
